// Copyright ©️ 2026 eVolve MEP, LLC

import React, { type ReactNode } from 'react';

import { ActionIcon, Flex, Title } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { useSetDocumentTitle } from 'components/Mantine/BasePageHeader';
import { GlobalFilters } from 'components/Mantine/GlobalFilters/GlobalFilters';
import type { PageBreadcrumbProps } from 'components/Mantine/Navigation/PageBreadcrumb';
import { useEvolveLocation } from 'components/Mantine/Navigation/useEvolveLocation';
import { getHistory, useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';
import { isNotNil } from 'helpers/isNotNil';

export type MobileBasePageHeaderProps = {
  /**
   * If `true`, will hide the header
   * @remarks Useful if the "header" is rendered manually further down the page
   * @defaultValue true if `rightSideComponent` is provided
   */
  hideHeader?: boolean;
  rightSideComponent?: ReactNode;
  hideGlobalFilters?: boolean;
} & PageBreadcrumbProps;

export const MobileBasePageHeader = ({
  title,
  parentPage,
  noHistory = false,
  rightSideComponent,
  hideHeader = isNotNil(rightSideComponent) && isNotNil(parentPage),
  hideGlobalFilters,
}: MobileBasePageHeaderProps) => {
  useSetDocumentTitle(title);
  const { state: locationState } = useEvolveLocation();
  const navigate = useEvolveNavigate(parentPage?.title ?? title, true, true);

  const hasLocationHistory = !noHistory && getHistory(locationState).length > 0;
  const hasParentPage = hasLocationHistory || isNotNil(parentPage);

  return (
    <Flex align="center" justify="space-between" gap="md" w="100%">
      {hasParentPage && (
        <ActionIcon
          variant="light"
          onClick={() => navigate(hasLocationHistory ? -1 : (parentPage?.url ?? -1))}
          size="input-sm"
        >
          <EvolveIcon icon="ArrowLeft" size="sm" />
        </ActionIcon>
      )}

      {!hideHeader && (
        <Title order={hasParentPage ? 5 : 3} c={hasParentPage ? 'dimmed' : undefined} lineClamp={1}>
          {title}
        </Title>
      )}

      <Flex align="center" justify="flex-end" wrap="wrap" gap="md" miw={35}>
        {rightSideComponent}
        {!hideGlobalFilters && <GlobalFilters />}
      </Flex>
    </Flex>
  );
};
